import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, ElementRef, inject, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Offcanvas } from 'bootstrap';
import { firstValueFrom, map } from 'rxjs';
import { PermissionDirective } from 'src/app/auth/directives/permission/permission.directive';
import { FEATURES } from 'src/app/auth/models/features';
import { PERMISSIONS } from 'src/app/auth/models/permissions';
import { FeatureService } from 'src/app/auth/services/features/feature.service';
import { ProfileService } from 'src/app/auth/services/profile.service';
import { IconButtonComponent } from 'src/app/shared/icon-button/icon-button.component';
import { FeedbackModalService, IFeedbackData } from 'src/app/shared/modals/service/feedback-modal.service';
import { AlertService } from 'src/app/shared/services/alert/alert.service';
import { HierarchyMenuService } from 'src/app/shared/services/hierarchy/hierarchy-menu/hierarchy-menu.service';
import { MenuService } from 'src/app/shared/services/menu/menu.service';
import { ViewportService } from 'src/app/shared/services/viewport/viewport.service';
import { HierarchyMenuComponent } from '../hierarchy-menu/hierarchy-menu.component';
import { MenuHeaderComponent } from '../menu-header/menu-header.component';
import { MenuItemComponent } from '../menu-item/menu-item.component';
import { MenuListComponent } from '../menu-list/menu-list.component';

@Component({
  selector: 'app-menu',
  imports: [
    MenuHeaderComponent,
    MenuItemComponent,
    MenuListComponent,
    PermissionDirective,
    NgTemplateOutlet,
    AsyncPipe,
    HierarchyMenuComponent,
    IconButtonComponent,
  ],
  templateUrl: './menu.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './menu.component.scss',
})
export class MenuComponent implements AfterViewInit {
  @ViewChild('offcanvas') private offcanvas!: ElementRef;
  @ViewChild('nav') private nav!: ElementRef;

  protected permissions = PERMISSIONS;
  protected loggedIn = inject(ProfileService)
    .getProfile()
    .pipe(map((profile) => !!profile));
  protected menuService = inject(MenuService);
  protected menuCollapsed$ = this.menuService.menuCollapsed$;
  protected hierarchyCollapsed$ = this.menuService.hierarchyCollapsed$;
  protected hierarchyMenuVisible$ = inject(HierarchyMenuService).enabled$;

  protected readonly viewport = inject(ViewportService);

  protected readonly topMenu: MenuEntry[] = [
    {
      list: true,
      id: 'dashboards',
      dropId: 'dropdownDashboards',
      title: 'Dashboards',
      icon: 'bi-columns-gap',
      permission: PERMISSIONS.dashboard.view,
      children: [
        { title: 'Energie', icon: 'bi-lightning-charge', link: '', exact: true },
        { title: 'Milieu', icon: 'bi-leaf', beta: true, feature: FEATURES.modules.environment.module },
        {
          title: 'Klimaat',
          icon: 'bi-thermometer-sun',
          offset: 0.2,
          beta: true,
          feature: FEATURES.modules.climate.module,
        },
        { title: 'Dashboard', icon: 'bi-clipboard-data' },
      ],
    },
    { title: 'Historie', icon: 'bi-clock-history', new: true, feature: FEATURES.modules.history.module },
    { title: 'Doelen', icon: 'bi-bullseye', permission: PERMISSIONS.target.view },
    { title: 'Alarmen', icon: 'bi-exclamation-triangle', permission: PERMISSIONS.alarm.view },
    { title: 'Contracten', icon: 'bi-file-earmark-text', beta: true, permission: PERMISSIONS.utilityContract.view },
    { title: 'Export', icon: 'bi-download', permission: PERMISSIONS.dashboard.exportData },
    { title: 'Configuratie', icon: 'bi-gear', permission: PERMISSIONS.configurator.view },
    { title: 'Gebruikers', icon: 'bi-people', permission: PERMISSIONS.user.view },
  ];

  protected readonly bottomMenu: MenuEntry[] = [
    { title: 'Alerts', icon: 'bi-exclamation-triangle', permission: PERMISSIONS.alert.view },
    { title: 'Beheerders', icon: 'bi-person-gear', permission: PERMISSIONS.user.viewGlobal },
    { title: 'Organisaties', icon: 'bi-diagram-3' },
  ];

  private feedbackModalService = inject(FeedbackModalService);
  private alertService = inject(AlertService);
  private readonly featureService = inject(FeatureService);

  ngAfterViewInit(): void {
    const offcanvasInstance = new Offcanvas(this.offcanvas.nativeElement);

    this.nav.nativeElement.addEventListener('transitionend', async () => {
      if (await firstValueFrom(this.menuService.menuCollapsed$)) {
        this.nav.nativeElement.classList.add('collapse-finished');
      }
    });

    this.menuService.menuCollapsed$.subscribe((collapsed) => {
      if (!collapsed) {
        this.nav?.nativeElement.classList.remove('collapse-finished');
      }
    });

    this.menuService.offcanvas$.subscribe(() => offcanvasInstance.toggle());
  }

  protected onFeedback(): void {
    this.feedbackModalService.openFeedbackModal().subscribe((feedbackData: IFeedbackData | null) => {
      if (!feedbackData) {
        return;
      }
      this.feedbackModalService.submitFeedback(feedbackData).subscribe((response) => {
        if (response) {
          this.alertService.addSuccess('Feedback verzonden', 'Bedankt voor uw feedback!');
        }
      });
    });
  }

  protected toggleHierarchy(): void {
    this.menuService.toggleHierarchyCollapsed();
  }

  protected hasFeature(feature?: string | string[]): boolean {
    return feature ? this.featureService.hasFeature(feature) : true;
  }
}

interface IMenuItem {
  title: string;
  icon: string;
  offset?: number;
  link?: string;
  exact?: boolean;
  beta?: boolean;
  new?: boolean;
  permission?: string | string[];
  feature?: string | string[];
}

interface IMenuList {
  list: true;
  id: string;
  dropId: string;
  title: string;
  icon: string;
  permission?: string | string[];
  feature?: string | string[];
  children: IMenuItem[];
}

type MenuEntry = IMenuList | IMenuItem;
